
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchEvent, EventStatus } from '@/services/eventService';
import { useToast } from "@/hooks/use-toast";
import { eventFormSchema, EventFormValues } from '../../schemas/eventFormSchema';
import { getFormDefaults } from './useFormDefaults';

interface UseEventDataProps {
  eventId: string | null;
  open: boolean;
  onClose: () => void;
}

export const useEventData = ({ eventId, open, onClose }: UseEventDataProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [originalValues, setOriginalValues] = useState<EventFormValues | null>(null);
  const { toast } = useToast();
  
  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: getFormDefaults(),
  });

  // Reset form when modal is closed
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  // Fetch event data or set defaults
  useEffect(() => {
    if (open && eventId) {
      const getEventDetails = async () => {
        try {
          setIsLoading(true);
          const event = await fetchEvent(eventId);
          if (event) {
            // Process dates
            let startDate = new Date();
            let endDate = new Date();
            let startTime = new Date();
            let endTime = new Date();
            let timeNotSpecified = true;

            if (event.dateStart && event.dateEnd) {
              startDate = new Date(event.dateStart);
              endDate = new Date(event.dateEnd);

              // Set times
              startTime = new Date(startDate);
              endTime = new Date(endDate);

              // Check if time is specified (not 00:00)
              timeNotSpecified = (startDate.getHours() === 0 && startDate.getMinutes() === 0 &&
                endDate.getHours() === 0 && endDate.getMinutes() === 0) ||
                event.timeNotSpecified === true;
            } else if (event.date) {
              // Only date specified, no time
              startDate = new Date(event.date);
              endDate = new Date(event.date);
              timeNotSpecified = true;

              // Default times
              startTime = new Date();
              startTime.setHours(9, 0, 0, 0);
              endTime = new Date();
              endTime.setHours(17, 0, 0, 0);
            }

            // Determine if event is online
            const isOnline = !!event.venue.online;
            
            // Fix for eventUrl - ensure it's not empty when online is true
            let eventUrl = isOnline ? (event.venue.url || "https://") : "";

            const formValues: EventFormValues = {
              eventName: event.name,
              description: event.description,
              startDate: startDate,
              endDate: endDate,
              startTime: startTime,
              endTime: endTime,
              isOnline: isOnline,
              city: event.venue.city || "",
              venueName: event.venue.name || "",
              address: event.venue.address || "",
              region: event.venue.region || "",
              eventUrl: eventUrl,
              eventType: event.type,
              creditPoints: event.creditPoints?.toString() || "",
              maxAttendees: event.maxAttendees?.toString() || "",
              imageUrl: event.imageUrl || "",
              postAccredited: event.postAccredited || false,
              professions: event.professions || [],
              timeNotSpecified: timeNotSpecified,
              registrationUrl: event.registrationUrl || "",
              status: event.status || EventStatus.DRAFT,
            };

            console.log('Setting form values:', formValues);

            // Set form values
            Object.entries(formValues).forEach(([key, value]) => {
              form.setValue(key as keyof EventFormValues, value);
            });

            setOriginalValues(formValues);

            // Reset dirty state after setting values
            form.reset(formValues);
          }
        } catch (error) {
          console.error('Error fetching event details:', error);
          toast({
            title: "Hiba történt!",
            description: "Nem sikerült betölteni az esemény adatait.",
            variant: "destructive",
          });
          onClose();
        } finally {
          setIsLoading(false);
        }
      };

      getEventDetails();
    } else if (open) {
      // For new events, set default values
      const defaultValues = getFormDefaults();
      form.reset(defaultValues);
      setOriginalValues(defaultValues);
    }
  }, [eventId, open, form, toast, onClose]);

  // Function to reset the form
  const resetForm = () => {
    if (originalValues) {
      form.reset(originalValues);
    } else {
      form.reset();
    }
  };
  
  return {
    isLoading,
    form,
    resetForm,
    originalValues,
  };
};
